// Sessions (docs/mastermind-admin-plan.md 3.3): the member's last 6 sessions in their first 12 months,
// newest first, with Show all. Each takes Present, Absent or Excused (the register's own write, audited);
// an absence takes "Caught up on" (hub.record_catch_up), with the hub's hint of when they watched the
// recording and last updated their actions; a session inside a pause says it isn't counted. The register
// note shows here, for staff only, and never on the log.
import { useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { Button, Card, DateField, adminPath, todayInUk, useAdmin } from '../ui/index.js'
import { dm, wdm } from '../../products/mastermind/log.js'
import { MM } from './words.js'
import { recordCatchUp, setMark } from './data.js'
import { Said } from './ThisMonth.jsx'

const MARKS = ['present', 'absent', 'excused']
const dayOf = t => new Date(t).toLocaleDateString('en-CA', { timeZone: 'Europe/London' })

function SessionRow({ s, person, first, ctx, onChanged }) {
  const { api } = useAdmin()
  const [busy, setBusy] = useState(null)
  const [line, setLine] = useState(null)
  const [on, setOn] = useState(s.caught_up_on || '')
  const day = wdm(s.starts_at)

  async function mark(status) {
    if (busy || status === s.status) return
    setBusy(status); setLine(null)
    try {
      await setMark(api, { session: s.session_id, person, status })
      await onChanged()
      setLine({ text: MM.markSaved(day, MM.markWords[status]) })
    } catch (e) { setLine({ bad: true, text: e.message }) } finally { setBusy(null) }
  }
  async function catchUp(value) {
    if (busy) return
    setBusy('catch'); setLine(null)
    try {
      const answer = await recordCatchUp(api, { session: s.session_id, person, on: value || null }, { ...ctx, dateLine: MM.caughtBadDate })
      await onChanged()
      setLine({ text: !value ? MM.caughtCleared : answer?.in_time ? MM.caughtInTime(day) : MM.caughtLate(answer?.days_after ?? '') })
    } catch (e) { setLine({ bad: true, text: e.message }) } finally { setBusy(null) }
  }
  const hint = s.hint ? MM.hint(s.hint.recording_watched_at ? dm(s.hint.recording_watched_at) : null, s.hint.last_update_at ? dm(s.hint.last_update_at) : null) : ''

  return (
    <li className={`amx-session amx-session--${s.result}`}>
      <div className="amx-session-head">
        <span className="amx-session-when">{MM.sessionWhen(day, s.cohort_name)}</span>
        {s.result === 'paused' && <span className="ad-tag">{MM.pausedLine}</span>}
        {!s.in_term && s.result !== 'paused' && <span className="ad-tag">{MM.outsideTerm}</span>}
      </div>
      <div className="amx-marks" role="group" aria-label={MM.marksLabel(day)}>
        {MARKS.map(m => (
          <Button key={m} size="xs" kind={s.status === m ? 'ink' : 'line'} aria-pressed={s.status === m} busy={busy === m} onClick={() => mark(m)}>{MM.markWords[m]}</Button>
        ))}
        {!s.status && <span className="amx-muted">{MM.noRegister}</span>}
        {!s.status && <Link to={adminPath.register(s.session_id)} className="amx-session-link">{MM.openRegister}</Link>}
      </div>
      {s.status === 'absent' && (
        <div className="amx-catch">
          <DateField label={MM.caughtUpOn} value={on} onChange={setOn} min={dayOf(s.starts_at)} max={todayInUk()} plain hint={hint || undefined} />
          <span className="amx-row-actions">
            <Button size="xs" kind="ink" busy={busy === 'catch'} disabled={!on || on === s.caught_up_on} onClick={() => catchUp(on)}>{MM.caughtSave}</Button>
            {s.caught_up_on && <Button size="xs" disabled={Boolean(busy)} onClick={() => { setOn(''); catchUp(null) }}>{MM.caughtClear}</Button>}
          </span>
          {s.caught_up_on && <span className="amx-muted">{MM.caughtLine(dm(s.caught_up_on), s.result === 'caught_up')}</span>}
        </div>
      )}
      {s.note && <span className="amx-note">{MM.registerNote(s.note)}</span>}
      <Said line={line} />
    </li>
  )
}

export function SessionsCard({ log, person, first, ctx, onChanged }) {
  const [all, setAll] = useState(false)
  const sessions = (log.sessions || []).slice().sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at))
  const inTerm = sessions.filter(s => s.in_term)
  const list = all ? sessions : inTerm.slice(0, 6)
  return (
    <Card id="mm-sessions" title={MM.sessionsH} className="amx-sessions">
      {!sessions.length && <p className="amx-muted">{MM.noSessionsYet(first)}</p>}
      {sessions.length > 0 && (
        <ul className="amx-session-list">
          {list.map(s => <SessionRow key={s.session_id} s={s} person={person} first={first} ctx={ctx} onChanged={onChanged} />)}
        </ul>
      )}
      {sessions.length > 6 && (
        <span><Button size="sm" kind="quiet" aria-expanded={all} onClick={() => setAll(a => !a)}>{all ? MM.showFewer : MM.showAll(sessions.length)}</Button></span>
      )}
    </Card>
  )
}
